import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CachedBuildingService, CurrencyService, UserService } from '@cfp/core';
import { BuildingMeasureTableItemDTOWithYearData } from '@cfp/features-shared/interfaces/year-data';
import { MeasurementService, MeasurementType } from '@cfp/shared/ui/measurement';
import { CellFormat, ExcelExportService, ExcelExportSheet } from '@cfp/ui/services';
import { TranslateService } from '@ngx-translate/core';
import {
  BuildingDetailsDTO,
  CurrencyCodeDTO,
  LabelGroupDTO,
  RegisteredOrCalculatedEnergyLabelDTO,
  RoadmapBuildingYearDTO
} from 'csr-asset-app';
import { UnitsCo2EmissionDTO, UserResponseDTO } from 'csr-login-app';
import { map } from 'rxjs/operators';
import { RoadmapPortfolioBuildingDetailsDTOWithYearData, Year } from '../interfaces';

export type RoadmapTableShowProperty =
  | 'investment'
  | 'costReduction'
  | 'co2ConsumptionReduction'
  | 'totalEnergyConsumptionReductionInKwh'
  | 'energyLabels';

@Injectable()
export class RoadmapTableExcelExportService {
  private readonly translateService = inject(TranslateService);
  private readonly excelExportService = inject(ExcelExportService);
  private readonly measurementService = inject(MeasurementService);
  private readonly currencyService = inject(CurrencyService);
  private readonly userService = inject(UserService);
  private readonly cachedBuildingService = inject(CachedBuildingService);

  private readonly customerName = toSignal(
    this.userService.get().pipe(map((user: UserResponseDTO) => user.primaryCustomer?.name))
  );

  public getExportSheet(
    rows: (RoadmapPortfolioBuildingDetailsDTOWithYearData | BuildingMeasureTableItemDTOWithYearData)[],
    years: Partial<RoadmapBuildingYearDTO>[] | Year[],
    totals: { [year: number]: { [property: string]: number } } | undefined,
    showProperty: RoadmapTableShowProperty,
    leftColumnName: 'name' | 'displayAddress',
    currencyCode: CurrencyCodeDTO | undefined,
    labelGroup: LabelGroupDTO | undefined,
    showM2: boolean,
    showEnergyLabelInRow: boolean
  ): ExcelExportSheet {
    const leftColumnHeader = this.getLeftColumnHeader(leftColumnName, showEnergyLabelInRow);
    const yearHeaders = years.map((year) => this.getYearHeader(year.year));
    const headers = [leftColumnHeader, ...yearHeaders];

    const format: { [key: string]: CellFormat } = {};
    const cellFormat = this.getCellFormat(showProperty, currencyCode, showM2);
    if (cellFormat) {
      yearHeaders.forEach((header) => {
        format[header] = cellFormat;
      });
    }

    const json = rows.map((row) => {
      const rowData: { [key: string]: string | number | undefined } = {
        [leftColumnHeader]: this.getLeftColumnValue(row, leftColumnName)
      };

      years.forEach((year, index) => {
        rowData[yearHeaders[index]] = this.mapCellValue(row, year.year, showProperty, labelGroup, currencyCode, showM2);
      });

      return rowData;
    });

    if (showProperty !== 'energyLabels' && rows.length > 0 && totals) {
      const totalsRow: { [key: string]: string | number | undefined } = {
        [leftColumnHeader]: this.translateService.instant(`roadmap.total.${showProperty}`)
      };

      years.forEach((year, index) => {
        const yearNumber = year.year;
        if (yearNumber && totals[yearNumber]) {
          totalsRow[yearHeaders[index]] = this.mapNumericValue(
            totals[yearNumber][showProperty],
            showProperty,
            currencyCode,
            showM2
          );
        } else {
          totalsRow[yearHeaders[index]] = undefined;
        }
      });

      json.push(totalsRow);
    }

    return { json, headers, format, name: this.getSheetName(showProperty) };
  }

  public export(
    rows: (RoadmapPortfolioBuildingDetailsDTOWithYearData | BuildingMeasureTableItemDTOWithYearData)[],
    years: Partial<RoadmapBuildingYearDTO>[] | Year[],
    totals: { [year: number]: { [property: string]: number } } | undefined,
    showProperty: RoadmapTableShowProperty,
    leftColumnName: 'name' | 'displayAddress',
    currencyCode: CurrencyCodeDTO | undefined,
    labelGroup: LabelGroupDTO | undefined,
    showM2: boolean,
    showEnergyLabelInRow: boolean,
    buildingId: number | undefined
  ): void {
    const exportSheet = this.getExportSheet(
      rows,
      years,
      totals,
      showProperty,
      leftColumnName,
      currencyCode,
      labelGroup,
      showM2,
      showEnergyLabelInRow
    );

    if (buildingId) {
      this.cachedBuildingService.v2BuildingsIdGet(buildingId).subscribe((building: BuildingDetailsDTO) => {
        const fileName = this.excelExportService.createFileName(
          `${this.customerName()}_${building.address?.display?.join('-') ?? ''}`,
          'roadmap'
        );
        this.excelExportService.export(fileName, [exportSheet]);
      });
      return;
    }

    const fileName = this.excelExportService.createFileName(this.customerName(), 'roadmap');
    this.excelExportService.export(fileName, [exportSheet]);
  }

  private getSheetName(showProperty: RoadmapTableShowProperty): string {
    const translationKey = this.getShowPropertyTranslationKey(showProperty);
    const translatedName = this.translateService
      .instant(translationKey)
      .replace(/<[^>]*>/g, '')
      .replace(/[\\/?*[\]:]/g, ' ')
      .trim();

    return translatedName.slice(0, 31) || showProperty;
  }

  private getShowPropertyTranslationKey(showProperty: RoadmapTableShowProperty): string {
    switch (showProperty) {
      case 'investment':
        return 'businesscase.investment';
      case 'costReduction':
        return 'costReduction';
      case 'co2ConsumptionReduction':
        return 'businesscase.co2reduction';
      case 'totalEnergyConsumptionReductionInKwh':
        return 'roadmap.totalEnergyConsumptionReductionInKwh';
      case 'energyLabels':
        return 'rating';
    }
  }

  private getLeftColumnHeader(leftColumnName: 'name' | 'displayAddress', showEnergyLabelInRow: boolean): string {
    if (leftColumnName === 'displayAddress') {
      return this.translateService.instant('building');
    }
    return this.translateService.instant(showEnergyLabelInRow ? 'roadmap.investment' : 'measure');
  }

  private getYearHeader(year: number | null | undefined): string {
    return year ? `${year}` : this.translateService.instant('noYear');
  }

  private getLeftColumnValue(
    row: RoadmapPortfolioBuildingDetailsDTOWithYearData | BuildingMeasureTableItemDTOWithYearData,
    leftColumnName: 'name' | 'displayAddress'
  ): string | undefined {
    if (leftColumnName === 'displayAddress') {
      return (row as RoadmapPortfolioBuildingDetailsDTOWithYearData).displayAddress?.join(', ');
    }
    return (row as BuildingMeasureTableItemDTOWithYearData).name;
  }

  private getCellFormat(
    showProperty: RoadmapTableShowProperty,
    currencyCode: CurrencyCodeDTO | undefined,
    showM2: boolean
  ): CellFormat | undefined {
    const perUnit = showM2 ? this.measurementService.squareUnitAsString() : undefined;

    switch (showProperty) {
      case 'investment':
      case 'costReduction': {
        const destinationCurrencyCode = currencyCode
          ? this.currencyService.getUserCurrencyCode(currencyCode)
          : undefined;
        return {
          type: 'FINANCIAL',
          currencyCode: destinationCurrencyCode,
          perUnit
        };
      }
      case 'co2ConsumptionReduction': {
        const unitsCo2Emission = this.userService.user()?.unitsCo2Emission || UnitsCo2EmissionDTO.METRICTONKG;
        return {
          type: 'NUMBER',
          perUnit,
          unit: showM2
            ? unitsCo2Emission === UnitsCo2EmissionDTO.METRICTONLB
              ? this.translateService.instant('measureUnitType.LB.KG')
              : this.translateService.instant('measureUnitType.KG.KG')
            : this.translateService.instant('tonCo2')
        };
      }
      case 'totalEnergyConsumptionReductionInKwh':
        return {
          type: 'NUMBER',
          unit: 'kWh',
          perUnit
        };
      default:
        return undefined;
    }
  }

  private mapCellValue(
    row: RoadmapPortfolioBuildingDetailsDTOWithYearData | BuildingMeasureTableItemDTOWithYearData,
    year: number | null | undefined,
    showProperty: RoadmapTableShowProperty,
    labelGroup: LabelGroupDTO | undefined,
    currencyCode: CurrencyCodeDTO | undefined,
    showM2: boolean
  ): string | number | undefined {
    if (showProperty === 'energyLabels') {
      return this.mapEnergyLabelValue(row, year, labelGroup);
    }

    const yearData = (row as BuildingMeasureTableItemDTOWithYearData).cols?.[year || -1] as
      | { [property: string]: unknown }
      | undefined;
    const value = yearData?.[showProperty];
    return this.mapNumericValue(value as number | undefined, showProperty, currencyCode, showM2);
  }

  private mapEnergyLabelValue(
    row: RoadmapPortfolioBuildingDetailsDTOWithYearData | BuildingMeasureTableItemDTOWithYearData,
    year: number | null | undefined,
    labelGroup: LabelGroupDTO | undefined
  ): string | undefined {
    if (!labelGroup) {
      return undefined;
    }

    const yearData = (row as BuildingMeasureTableItemDTOWithYearData).cols?.[year || -1] as
      | { [property: string]: unknown }
      | undefined;
    const energyLabels = yearData?.['energyLabels'] as
      | { [key: string]: RegisteredOrCalculatedEnergyLabelDTO }
      | undefined;

    if (year && energyLabels?.[labelGroup]?.label) {
      return energyLabels[labelGroup].label;
    }

    if (!year) {
      const buildingRow = row as RoadmapPortfolioBuildingDetailsDTOWithYearData;
      const currentLabel = buildingRow.energyLabels?.[labelGroup]?.currentEnergyLabel?.label;
      const improvedLabel = buildingRow.energyLabels?.[labelGroup]?.improvedEnergyLabel?.label;
      if (currentLabel && improvedLabel) {
        return `${currentLabel} → ${improvedLabel}`;
      }
    }

    return undefined;
  }

  private mapNumericValue(
    value: number | undefined,
    showProperty: RoadmapTableShowProperty,
    currencyCode: CurrencyCodeDTO | undefined,
    showM2: boolean
  ): number | undefined {
    if (value === undefined || value === null || value === 0) {
      return undefined;
    }

    switch (showProperty) {
      case 'investment':
      case 'costReduction': {
        const convertedAreaValue = showM2
          ? this.measurementService.convertAreaFromMeter2(MeasurementType.VALUE_PER_SQUARE, value)
          : value;
        const convertedCurrencyValue = this.currencyService.convertToUserCurrency(convertedAreaValue, currencyCode);
        return showM2 ? Math.round(convertedCurrencyValue * 100) / 100 : Math.round(convertedCurrencyValue);
      }
      case 'co2ConsumptionReduction': {
        if (showM2) {
          const unitsCo2Emission = this.userService.user()?.unitsCo2Emission || UnitsCo2EmissionDTO.METRICTONKG;
          let convertedValue = this.measurementService.convertAreaFromMeter2(MeasurementType.VALUE_PER_SQUARE, value);
          if (unitsCo2Emission === UnitsCo2EmissionDTO.METRICTONLB) {
            convertedValue = convertedValue * 2204.6;
          } else {
            convertedValue = convertedValue * 1000;
          }
          return Math.round(convertedValue * 100) / 100;
        }
        return value;
      }
      case 'totalEnergyConsumptionReductionInKwh':
        return showM2 ? this.measurementService.convertAreaFromMeter2(MeasurementType.VALUE_PER_SQUARE, value) : value;
      default:
        return value;
    }
  }
}
